import http from './httpClient';

/**
 * @typedef {Object} Ingredient
 * @property {string} id
 * @property {string} name
 * @property {string} category
 * @property {string} unit
 * @property {number} costPerUnit
 * @property {number} leadTimeDays
 * @property {number} minOrderQty
 * @property {string} updatedAt
 */

/**
 * Ingredient API service
 */
const ingredientApi = {
  /**
   * List ingredients
   * @param {Object} params
   * @returns {Promise<{items: Ingredient[], total: number}>}
   */
  async list(params = {}) {
    const { data } = await http.get('/api/v1/ingredients/', { params });
    return data;
  },

  /**
   * Get ingredient by id
   * @param {string} id
   * @returns {Promise<Ingredient>}
   */
  async get(id) {
    const { data } = await http.get(`/api/v1/ingredients/${id}`);
    return data;
  },

  /**
   * Create ingredient
   * @param {Partial<Ingredient>} payload
   * @returns {Promise<Ingredient>}
   */
  async create(payload) {
    const { data } = await http.post('/api/v1/ingredients/', payload);
    return data;
  },

  /**
   * Update ingredient
   * @param {string} id
   * @param {Partial<Ingredient>} payload
   * @returns {Promise<Ingredient>}
   */
  async update(id, payload) {
    const { data } = await http.put(`/api/v1/ingredients/${id}`, payload);
    return data;
  },

  /**
   * Delete ingredient
   * @param {string} id
   * @returns {Promise<void>}
   */
  async remove(id) {
    await http.delete(`/api/v1/ingredients/${id}`);
  },

  /**
   * Search by name
   * @param {string} q
   * @returns {Promise<Ingredient[]>}
   */
  async searchByName(q) {
    const { data } = await http.get('/api/v1/ingredients/search', { params: { q } });
    return data;
  },

  /**
   * List the products that use this ingredient, and how much per keg
   * @param {string} id
   * @returns {Promise<{ingredient_id: number, ingredient_name: string, products: Array}>}
   */
  async getProducts(id) {
    const { data } = await http.get(`/api/v1/ingredients/${id}/products`);
    return data;
  },

  /**
   * List categories
   * @returns {Promise<string[]>}
   */
  async listCategories() {
    const { data } = await http.get('/api/v1/ingredients/categories/list');
    return data?.categories ?? data;
  },

  /**
   * List units
   * @returns {Promise<string[]>}
   */
  async listUnits() {
    const { data } = await http.get('/api/v1/ingredients/units/list');
    return data?.units ?? data;
  },

  /**
   * Low stock / reorder alerts
   * @param {Object} params
   * @returns {Promise<any>}
   */
  async alerts(params = {}) {
    const { data } = await http.get('/api/v1/ingredients/alerts', { params });
    return data;
  },

  /**
   * Cost analytics
   * @param {Object} params
   * @returns {Promise<any>}
   */
  async costAnalytics(params = {}) {
    const { data } = await http.get('/api/v1/ingredients/costs/analytics', { params });
    return data;
  },

  /**
   * Update cost for a single ingredient
   * @param {string} id
   * @param {number} costPerUnit
   * @returns {Promise<Ingredient>}
   */
  async updateCost(id, costPerUnit) {
    const { data } = await http.put(`/api/v1/ingredients/${id}/cost`, { costPerUnit });
    return data;
  },

  /**
   * Bulk update costs
   * @param {Array<{id: string, costPerUnit: number}>} items
   * @returns {Promise<any>}
   */
  async bulkUpdateCosts(items) {
    const { data } = await http.put('/api/v1/ingredients/costs', { items });
    return data;
  }
};

export default ingredientApi;